import { Divider, Stack, Tooltip } from '@mantine/core';
import { LinkProps } from '@tanstack/react-router';

import NavbarLink from '../navbar-link/navbar-link';

import classes from './navbar-section.module.css';

export type NavbarLinkConfig = {
  label: string;
  to: LinkProps['to'];
  icon: React.FC<{ stroke?: string | number; style?: React.CSSProperties }>;
  hasAccess?: boolean;
};

export function filterNavbarLinks(links: NavbarLinkConfig[]) {
  return links
    .filter((link) => {
      if (typeof link.hasAccess === 'undefined') {
        return true;
      }

      return link.hasAccess;
    })
    .map(({ hasAccess: _hasAccess, ...link }) => link);
}

export interface NavbarSectionProps {
  links: NavbarLinkConfig[];
  /** Horizontal rule before this group. */
  withDivider?: boolean;
  /** Keep the divider visible when the link list is still empty (e.g. WIP). */
  showDividerWhenEmpty?: boolean;
  /** Short label on the divider (shown in a tooltip on narrow navbars). */
  sectionLabel?: string;
  /** Corner tag on each link icon (e.g. "WIP"). */
  linkCornerBadge?: string;
}

export function NavbarSection({
  links,
  withDivider = false,
  showDividerWhenEmpty = false,
  sectionLabel,
  linkCornerBadge,
}: NavbarSectionProps) {
  const filtered = filterNavbarLinks(links);
  const showSection = filtered.length > 0 || showDividerWhenEmpty;

  if (!showSection) {
    return null;
  }

  const showDivider =
    withDivider && (filtered.length > 0 || showDividerWhenEmpty);

  const divider = showDivider ? (
    <Divider className={classes.divider} />
  ) : null;

  return (
    <Stack gap="xs">
      {showDivider && sectionLabel ? (
        <Tooltip label={sectionLabel} position="right">
          <div>{divider}</div>
        </Tooltip>
      ) : (
        divider
      )}
      {filtered.map((link) => (
        <NavbarLink
          {...link}
          cornerBadge={linkCornerBadge}
          key={link.label}
        />
      ))}
    </Stack>
  );
}

export default NavbarSection;
